import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Button, Drawer, Dropdown } from 'antd';
import {
    DashboardOutlined, CarOutlined, CalendarOutlined,
    UserOutlined, BellOutlined, MenuUnfoldOutlined,
    MenuFoldOutlined, SettingOutlined, LogoutOutlined,
    HistoryOutlined, BarChartOutlined, DollarOutlined,
    MenuOutlined, GlobalOutlined, CloseOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Sider, Content } = Layout;

const SidebarLayout = ({ children }) => {
    const { t, i18n } = useTranslation();
    const [collapsed, setCollapsed] = useState(false);
    const [userRole, setUserRole] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const role = localStorage.getItem('userRole');
        setUserRole(role || '');
        
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setMobileMenuOpen(false);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleCollapsed = () => setCollapsed(!collapsed);

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        document.body.dir = lng === 'ar' ? 'rtl' : 'ltr';
    };

    const languageMenu = {
        items: [
            { key: 'fr', label: 'Français', onClick: () => changeLanguage('fr') },
            { key: 'ar', label: 'العربية', onClick: () => changeLanguage('ar') },
            { key: 'en', label: 'English', onClick: () => changeLanguage('en') },
        ]
    };

    const commonMenuItems = [
        { key: '/dashboard', icon: <DashboardOutlined />, label: t('dashboard') },
        { key: '/voitures', icon: <CarOutlined />, label: t('vehicles') },
        { key: '/clients', icon: <UserOutlined />, label: t('clients') },
        { key: '/reservations', icon: <CalendarOutlined />, label: t('reservations') },
        { key: '/location', icon: <CarOutlined />, label: t('rental') },
        { key: '/historique', icon: <HistoryOutlined />, label: t('history') },
        { key: '/entretien', icon: <CarOutlined />, label: t('maintenance') },
        { key: '/calendar', icon: <CalendarOutlined />, label: t('calendar') },
    ];

    const adminMenuItems = [
        { key: '/finance', icon: <DollarOutlined />, label: t('finance') },
        { key: '/CarTransactions', icon: <BarChartOutlined />, label: t('vehicle_stats') },
        { key: '/statistics', icon: <BarChartOutlined />, label: t('general_stats') },
        { key: '/account', icon: <SettingOutlined />, label: t('account') },
    ];

    const menuItems = [
        ...commonMenuItems,
        ...(userRole === 'admin' ? adminMenuItems : [])
    ];

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        navigate('/');
    };

    const bottomMenuItems = [
        { key: 'logout', icon: <LogoutOutlined />, label: t('logout'), danger: true }
    ];
    
    const handleMenuNavigate = (key) => {
        navigate(key);
        if (isMobile) setMobileMenuOpen(false);
    };

    const UserRoleLanguageHeader = () => (
        <div style={{
            padding: '12px 16px', color: '#94A3B8', fontSize: '13px',
            textTransform: 'uppercase', borderBottom: '1px solid #334155',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
            <span>{userRole === 'admin' ? t('admin') : t('employee')}</span>
            <Dropdown menu={languageMenu} placement="bottomRight" trigger={['click']}>
                <Button type="text" size="small" icon={<GlobalOutlined />} style={{ color: '#F1F5F9', padding: "0 8px", background: 'rgba(255,255,255,0.1)' }} />
            </Dropdown>
        </div>
    );

    const MenuContent = () => (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{
                flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '10px 0',
                scrollbarWidth: 'thin', scrollbarColor: '#475569 transparent',
            }}>
                <Menu
                    theme="dark" mode="inline" selectedKeys={[location.pathname]}
                    items={menuItems} onSelect={({ key }) => handleMenuNavigate(key)}
                    style={{ background: 'transparent', border: 'none' }}
                />
            </div>
            <div style={{ flexShrink: 0, borderTop: '1px solid #334155' }}>
                <Menu
                    theme="dark" mode="inline" selectedKeys={[]}
                    items={bottomMenuItems} onSelect={({ key }) => { if (key === 'logout') handleLogout(); }}
                    style={{ background: 'transparent', border: 'none' }}
                />
                {!isMobile && (
                    <div style={{ padding: '16px', textAlign: 'center', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={toggleCollapsed} style={{ color: 'white', fontSize: '16px', display: 'flex', justifyContent: 'center', width: '100%' }} />
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Layout style={{ minHeight: '100vh', flexDirection: isMobile ? 'column' : 'row', overflowX: 'hidden', maxWidth: '100vw' }}>
            {/* MOBILE TOP NAVBAR */}
            {isMobile && (
                <div style={{
                    height: '64px', background: '#1E293B', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', padding: '0 16px', position: 'sticky',
                    top: 0, zIndex: 1000, width: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar size={36} src="/logo.png" style={{ backgroundColor: '#3B82F6' }} />
                        <span style={{ color: 'white', fontWeight: 'bold', fontSize: '18px', marginLeft: '12px' }}>Dominosoft</span>
                    </div>
                    <Button 
                        type="text" 
                        icon={<MenuOutlined style={{ color: 'white', fontSize: '22px' }} />} 
                        onClick={() => setMobileMenuOpen(true)}
                    />
                </div>
            )}

            {/* DESKTOP SIDER */}
            {!isMobile && (
                <Sider
                    collapsible collapsed={collapsed} trigger={null} width={250} theme="dark"
                    style={{
                        background: '#1E293B', position: 'fixed', height: '100vh', zIndex: 1000, overflow: 'hidden',
                        left: i18n.language === 'ar' ? 'auto' : 0, right: i18n.language === 'ar' ? 0 : 'auto',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #334155', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Avatar size={40} src="/logo.png" style={{ backgroundColor: '#3B82F6' }} />
                            {!collapsed && (
                                <span style={{ color: 'white', fontWeight: 'bold', fontSize: '18px', marginLeft: i18n.language === 'ar' ? '0' : '10px', marginRight: i18n.language === 'ar' ? '10px' : '0' }}>
                                    Dominosoft
                                </span>
                            )}
                        </div>
                        <UserRoleLanguageHeader />
                        <MenuContent />
                    </div>
                </Sider>
            )}

            {/* MOBILE DRAWER */}
            <Drawer
                title={<div style={{ color: 'white', display: 'flex', alignItems: 'center' }}><Avatar size={32} src="/logo.png" style={{ backgroundColor: '#3B82F6', marginRight: 10 }} /> Dominosoft</div>}
                placement={i18n.language === 'ar' ? 'right' : 'left'}
                onClose={() => setMobileMenuOpen(false)}
                open={mobileMenuOpen}
                width={280}
                styles={{
                    body: { padding: 0, background: '#1E293B', display: 'flex', flexDirection: 'column' },
                    header: { background: '#0F172A', borderBottom: '1px solid #334155' }
                }}
                closeIcon={<CloseOutlined style={{ color: 'white' }} />}
            >
                <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                    <UserRoleLanguageHeader />
                    <MenuContent />
                </div>
            </Drawer>

            {/* MAIN CONTENT AREA */}
            <Content style={{
                marginLeft: isMobile ? 0 : (i18n.language === 'ar' ? 0 : (collapsed ? 80 : 250)),
                marginRight: isMobile ? 0 : (i18n.language === 'ar' ? (collapsed ? 80 : 250) : 0),
                padding: isMobile ? '8px' : '20px',
                background: '#F8FAFC',
                minHeight: isMobile ? 'calc(100vh - 64px)' : '100vh',
                transition: 'margin 0.2s',
                overflowX: 'auto',
                overflowY: 'auto',
                maxWidth: '100vw',
                boxSizing: 'border-box',
                minWidth: 0,
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
            }}>
                <div style={{ minHeight: '100%' }}>
                    {children}
                </div>
            </Content>
        </Layout>
    );
};

export default SidebarLayout;